import { cancelOpenPipelineCheckIn } from './checkInService';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ref, get, update } from 'firebase/database';
import { database } from '../firebaseConfig';

// Links a CRM contact to a new transaction.
// Contacts -> "Create Transaction" opens /transactions/new?contact=<id>; the type picker passes it on
// to the form, which pre-fills the client and, after the normal save, stores the link both ways:
//   users/{uid}/transactions/{tid}/contactIds/{cid} = true
//   crm/contacts/{cid}/transactionIds/{tid} = { ownerUid, type, label, at }
// Nothing else is created; existing transactions are never touched.

export const useContactPrefill = (setFormData, enabled = true) => {
  const [params] = useSearchParams();
  const contactId = enabled ? params.get('contact') : null;
  const [contact, setContact] = useState(null);
  useEffect(() => {
    if (!contactId) return;
    get(ref(database, `crm/contacts/${contactId}`)).then((snap) => {
      const c = snap.val();
      if (!c) return;
      setContact({ id: contactId, ...c });
      const parts = (c.name || '').trim().split(/\s+/);
      const firstName = c.firstName || (c.lastName ? '' : parts.shift() || '');
      const lastName = c.lastName || (c.firstName ? '' : parts.join(' '));
      setFormData((prev) => ({
        ...prev,
        firstName: prev.firstName || firstName,
        lastName: prev.lastName || lastName,
        email: prev.email || c.email || '',
        phone: prev.phone || c.phone || '',
      }));
    }).catch((err) => console.error('Could not load contact for prefill', err));
  }, [contactId, setFormData]);
  return contact;
};

export const linkContactToTransaction = async (contact, uid, transactionId, type, label) => {
  if (!contact?.id || !uid || !transactionId) return;
  try {
    await update(ref(database), {
      [`users/${uid}/transactions/${transactionId}/contactIds/${contact.id}`]: true,
      [`crm/contacts/${contact.id}/transactionIds/${transactionId}`]: { ownerUid: uid, type: type || '', label: label || '', at: new Date().toISOString() },
    });
  } catch (err) {
    console.error('Could not link contact and transaction', err);
  }
};

// Pipeline -> transaction ("Create Landlord transaction" etc.). The pipeline record passes
// ?pipeline=<seller|buyer>:<recordId>. After the normal save, the form records
// crm/{sellerInterests|buyerInterests}/{id}/convertedTransactions/{Type} = { transactionId, ownerUid, at },
// so one person can have a Seller and a Landlord transaction at the same time, both linked to them.
const pipelineBase = (kind) => (kind === 'buyer' ? 'crm/buyerInterests' : 'crm/sellerInterests');

export const usePipelineSource = (setFormData, enabled = true, withAddress = true) => {
  const [params] = useSearchParams();
  const raw = enabled ? params.get('pipeline') || '' : '';
  const [kind, id] = raw.split(':');
  const src = id ? { kind, id } : null;
  useEffect(() => {
    if (!id || !setFormData) return;
    get(ref(database, `${pipelineBase(kind)}/${id}`)).then((snap) => {
      const r = snap.val();
      if (!r) return;
      setFormData((prev) => ({
        ...prev,
        firstName: prev.firstName || r.firstName || '',
        lastName: prev.lastName || r.lastName || '',
        email: prev.email || r.email || '',
        phone: prev.phone || r.phone || '',
        propertyAddress: prev.propertyAddress || (kind === 'buyer' || !withAddress ? '' : r.propertyAddress || ''),
      }));
    }).catch((err) => console.error('Could not load pipeline record for prefill', err));
  }, [kind, id, setFormData, withAddress]);
  return src;
};

export const markPipelineConverted = async (src, type, transactionId, uid) => {
  if (!src?.id || !type || !transactionId) return;
  try {
    await update(ref(database, `${pipelineBase(src.kind)}/${src.id}/convertedTransactions`), {
      [type]: { transactionId, ownerUid: uid || null, at: new Date().toISOString() },
    });
    // Converting a pipeline buyer into a transaction ends its check-ins.
    if (src.kind === 'buyer') await cancelOpenPipelineCheckIn(src.id);
  } catch (err) {
    console.error('Could not link the pipeline record to the transaction', err);
  }
};
